import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, BookOpen, ArrowRight, CheckCircle, ShoppingCart, Sparkles, Gamepad2, HelpCircle, BookText, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kajabi purchase link
const KAJABI_PURCHASE_URL = "https://vittafinance.mykajabi.com/offers/4ZXUkaxV";

// Element tiles configuration - 8 elements total
const ELEMENT_TILES = [
  { id: 'element-1', title: 'Element 1: Derivatives Market Structure', description: 'Overview of derivatives markets, exchanges, and regulatory framework', icon: '🏛️', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' },
  { id: 'element-2', title: 'Element 2: Options Fundamentals', description: 'Call and put options, basic strategies, and option pricing principles', icon: '📊', color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' },
  { id: 'element-3', title: 'Element 3: Advanced Options Strategies', description: 'Spreads, straddles, strangles, and complex multi-leg strategies', icon: '🎯', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
  { id: 'element-4', title: 'Element 4: Futures & Forwards', description: 'Futures contracts, forwards, basis risk, and hedging applications', icon: '📈', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
  { id: 'element-5', title: 'Element 5: Swaps & OTC Derivatives', description: 'Interest rate swaps, currency swaps, and over-the-counter derivatives', icon: '🔄', color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' },
  { id: 'element-6', title: 'Element 6: Risk Management', description: 'Greeks, delta hedging, portfolio risk management, and VaR', icon: '🛡️', color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
  { id: 'element-7', title: 'Element 7: Pricing Models', description: 'Black-Scholes, binomial trees, and derivatives valuation models', icon: '🔢', color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' },
  { id: 'element-8', title: 'Element 8: Regulatory & Compliance', description: 'CIRO regulations, margin requirements, and compliance obligations', icon: '⚖️', color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' },
  { id: 'mock-exam', title: 'Derivatives Mock Exam with Diagnostics', description: 'Comprehensive practice exam covering all 8 elements with detailed performance analysis', icon: '🎓', color: '#d946ef', gradient: 'linear-gradient(135deg, #d946ef 0%, #c026d3 100%)', isMockExam: true },
];

const DerivativesInteractiveBook = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');

  const PRICE = 49.99;
  const ORIGINAL_PRICE = 129.99;

  // Check access on mount if token is in URL
  useEffect(() => {
    if (tokenFromUrl) {
      verifyAccess(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const verifyAccess = async (accessToken, userEmail = '') => {
    setCheckingAccess(true);
    setError('');
    
    try {
      const response = await fetch(
        `${API_URL}/api/books/derivatives-interactive/verify-access?token=${accessToken}&email=${encodeURIComponent(userEmail)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setHasAccess(true);
          setShowAccessForm(false);
          if (data.first_name) setUserName(data.first_name);
          localStorage.setItem('derivatives_access_token', accessToken);
          setSearchParams({ token: accessToken });
        } else {
          setError('Invalid access credentials. Please check your email and token.');
        }
      } else {
        setError('Unable to verify access. Please try again.');
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Connection error. Please try again.');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    
    if (!email || !token) {
      setError('Please enter both email and access token.');
      return;
    }
    
    verifyAccess(token, email);
  };

  const handleElementClick = (elementId, isMockExam = false) => {
    if (!hasAccess) return;
    let url;
    if (isMockExam) {
      url = `/derivatives-mock-exam?token=${tokenFromUrl || token}`;
    } else {
      url = `/books/derivatives-interactive/${elementId}?token=${tokenFromUrl || token}`;
    }
    window.open(url, '_blank');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1e1b4b 0%, #312e81 100%)'
    }}>
      {/* Hero Section */}
      <div style={{
        padding: '80px 24px 60px',
        maxWidth: '1200px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '100px',
          padding: '8px 20px',
          marginBottom: '24px'
        }}>
          <Sparkles size={16} color="#8b5cf6" />
          <span style={{ color: '#a78bfa', fontSize: '14px', fontWeight: '600' }}>
            April 2025 Special Pricing
          </span>
        </div>

        <h1 style={{
          fontSize: '56px',
          fontWeight: '900',
          color: 'white',
          marginBottom: '24px',
          lineHeight: '1.1'
        }}>
          Derivatives Quick Study<br />
          <span style={{ 
            background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Interactive Guide
          </span>
        </h1>

        <p style={{
          fontSize: '20px',
          color: '#c4b5fd',
          maxWidth: '700px',
          margin: '0 auto 32px',
          lineHeight: '1.6'
        }}>
          Master CIRO's Derivatives exam with Canada's most interactive study experience. 
          8 comprehensive elements plus full mock exam with detailed reading notes, interactive games, flashcards, and quizzes.
        </p>

        {/* Pricing Display */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{
            fontSize: '24px',
            color: '#6b7280',
            textDecoration: 'line-through'
          }}>
            ${ORIGINAL_PRICE.toFixed(2)} CAD
          </div>
          <div style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#a78bfa'
          }}>
            ${PRICE.toFixed(2)} CAD
          </div>
          <div style={{
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '8px',
            padding: '8px 16px',
            color: '#a78bfa',
            fontSize: '16px',
            fontWeight: '700'
          }}>
            Save ${(ORIGINAL_PRICE - PRICE).toFixed(0)}!
          </div>
        </div>

        {!hasAccess && !showAccessForm && (
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: '700',
                textDecoration: 'none',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <ShoppingCart size={20} />
              Buy Now - ${PRICE.toFixed(2)} CAD
            </a>

            <button
              onClick={() => setShowAccessForm(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: '600',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            >
              <Lock size={20} />
              I Have an Access Code
            </button>
          </div>
        )}

        {showAccessForm && !hasAccess && (
          <div style={{
            maxWidth: '500px',
            margin: '0 auto',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '32px',
            textAlign: 'left'
          }}>
            <h3 style={{ color: 'white', fontSize: '24px', marginBottom: '8px' }}>
              Enter Your Access Code
            </h3>
            <p style={{ color: '#c4b5fd', fontSize: '14px', marginBottom: '24px' }}>
              Your access code will be emailed to you after registration on Kajabi
            </p>

            <form onSubmit={handleAccessSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#e0e7ff', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#e0e7ff', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Access Token
                </label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter your access token"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                />
              </div>

              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: '#fca5a5',
                  fontSize: '14px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  disabled={checkingAccess}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: checkingAccess ? 'not-allowed' : 'pointer',
                    opacity: checkingAccess ? 0.6 : 1,
                    transition: 'opacity 0.2s'
                  }}
                >
                  {checkingAccess ? 'Verifying...' : 'Access Content'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowAccessForm(false);
                    setError('');
                    setEmail('');
                    setToken('');
                  }}
                  style={{
                    padding: '14px 24px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#c4b5fd',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {hasAccess && (
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '16px',
            padding: '24px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <CheckCircle size={32} color="#a78bfa" />
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ color: 'white', fontSize: '18px', marginBottom: '4px' }}>
                Welcome{userName ? `, ${userName}` : ''}! 🎉
              </h3>
              <p style={{ color: '#c4b5fd', fontSize: '14px', margin: 0 }}>
                You now have full access to all Derivatives study elements. Click any element below to begin.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Features Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px 60px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '60px'
        }}>
          {[
            { icon: <BookOpen size={24} color="#a78bfa" />, title: 'Reading Notes', desc: 'Comprehensive study notes for each topic' },
            { icon: <Gamepad2 size={24} color="#ec4899" />, title: 'Interactive Games', desc: 'Learn through engaging games and simulations' },
            { icon: <BookText size={24} color="#f59e0b" />, title: 'Flashcards', desc: 'Quick review with digital flashcards for key concepts' },
            { icon: <HelpCircle size={24} color="#10b981" />, title: 'Exam Quizzes', desc: 'Practice with realistic exam-style questions' },
          ].map((feature, idx) => (
            <div key={idx} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ marginBottom: '16px' }}>{feature.icon}</div>
              <h3 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                {feature.title}
              </h3>
              <p style={{ color: '#c4b5fd', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                {feature.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Elements Grid */}
        <h2 style={{
          color: 'white',
          fontSize: '32px',
          fontWeight: '800',
          marginBottom: '32px',
          textAlign: 'center'
        }}>
          Study Elements {hasAccess && '(Click to Access)'}
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '24px'
        }}>
          {ELEMENT_TILES.map((element) => (
            <div
              key={element.id}
              onClick={() => handleElementClick(element.id, element.isMockExam)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '24px',
                cursor: hasAccess ? 'pointer' : 'default',
                transition: 'all 0.3s',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => {
                if (hasAccess) {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = element.color;
                  e.currentTarget.style.boxShadow = `0 8px 24px ${element.color}30`;
                }
              }}
              onMouseOut={(e) => {
                if (hasAccess) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: element.gradient
              }} />

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  fontSize: '48px',
                  lineHeight: 1
                }}>
                  {element.icon}
                </div>
                
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '700',
                    marginBottom: '8px',
                    lineHeight: '1.3'
                  }}>
                    {element.title}
                  </h3>
                  
                  <p style={{
                    color: '#c4b5fd',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {element.description}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {hasAccess ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: element.color,
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Start Learning
                    <ArrowRight size={16} />
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#6b7280',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    <Lock size={16} />
                    Locked
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        {!hasAccess && (
          <div style={{
            marginTop: '80px',
            textAlign: 'center',
            background: 'rgba(139, 92, 246, 0.05)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '20px',
            padding: '48px 32px'
          }}>
            <h2 style={{
              color: 'white',
              fontSize: '36px',
              fontWeight: '900',
              marginBottom: '16px'
            }}>
              Ready to Master Derivatives?
            </h2>
            
            <p style={{
              color: '#c4b5fd',
              fontSize: '18px',
              maxWidth: '600px',
              margin: '0 auto 32px',
              lineHeight: '1.6'
            }}>
              Join successful candidates who used our interactive study guides. 
              April special pricing ends soon!
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '20px',
                color: '#6b7280',
                textDecoration: 'line-through'
              }}>
                ${ORIGINAL_PRICE.toFixed(2)} CAD
              </div>
              <div style={{
                fontSize: '40px',
                fontWeight: '900',
                color: '#a78bfa'
              }}>
                ${PRICE.toFixed(2)} CAD
              </div>
            </div>

            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                color: 'white',
                padding: '18px 36px',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '700',
                textDecoration: 'none',
                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <ShoppingCart size={24} />
              Get Started Now
            </a>
          </div>
        )}

        {/* Back to Books */}
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <Link
            to="/books"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#c4b5fd',
              fontSize: '16px',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#e0e7ff'}
            onMouseOut={(e) => e.currentTarget.style.color = '#c4b5fd'}
          >
            <BookOpen size={20} />
            Back to All Study Guides
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DerivativesInteractiveBook;
